import Reactor from 'data/reactor';

import RuntimeGetters from 'data/app-runtime/getters';
import PartnerGetters from 'data/partner/getters';
import RosterGetters from 'data/roster/getters';
import LCGetters from 'data/learning-continuum/getters';
import TermUtils from 'utils/terms';
import CommonValues from 'resources/common-values';
import {
    findNextStudent,
    findPreviousStudent,
    findMostRecentTestEventsBySubject,
    findTestEventForTermAndSubject,
    ritQuintilesLoaded,
    districtAveragesLoaded,
    fieldLoadedForStudent,
    growthGoalNormsLoaded,
} from './utils.js';

const store = 'student';

const students = [store, 'students'];

const allDataForStudent = (studentBid) => students.concat([studentBid]);

const testEventsForCurrentStudent = [
    students,
    RosterGetters.selectedStudentBid,
    (s, sBid) => (s ? s.getIn([sBid, 'testEvents'], null) : null),
];

const testEventsForStudentBid = (studentBid) => {
    return [students, (s) => (s ? s.getIn([studentBid, 'testEvents'], null) : null)];
};

export const currentlySelectedTermNumber = [
    students,
    RosterGetters.selectedStudentBid,
    (s, studentBid) => (s ? s.getIn([studentBid, 'selectedTermNumber'], null) : null),
];

export const measurementScaleIDsForStudentByBid = (studentBid) => {
    return ['students', studentBid, 'measurementScaleIDsForStudent'];
};

/* It's worth noting that batch print does not set up the stores correctly and this will not work - use [] instead */
const getKeystoneTestEvent = [
    testEventsForCurrentStudent,
    currentlySelectedTermNumber,
    RuntimeGetters.selectedSubject,
    (testEvents, termNumber, subject) => {
        let keystoneTestEvent = null;
        if (testEvents && testEvents.size !== 0) {
            if (termNumber === CommonValues.mostRecentTermNumber) {
                const mostRecentTestEventsBySubject = findMostRecentTestEventsBySubject(testEvents);
                keystoneTestEvent = mostRecentTestEventsBySubject.get(subject.get('bid'));
            } else {
                keystoneTestEvent = findTestEventForTermAndSubject(testEvents, termNumber, subject.get('bid'));
            }
        }
        return keystoneTestEvent;
    },
];

const nextStudent = (studentBid) => {
    return [
        RosterGetters.students,
        RosterGetters.selectedStudentBid,
        (studentList, currentBid) => {
            const sBid = studentBid ? studentBid : currentBid;
            return findNextStudent(studentList, sBid);
        },
    ];
};

const previousStudent = (studentBid) => {
    return [
        RosterGetters.students,
        RosterGetters.selectedStudentBid,
        (studentList, currentBid) => {
            const sBid = studentBid ? studentBid : currentBid;
            return findPreviousStudent(studentList, sBid);
        },
    ];
};

/**
 * Given a student list this will return the list of terms with test events for the given student bid
 * @param studentList The overall list of students
 * @param studentBid The unique identifier for the student being sought
 * @returns A list of term numbers which have test events for the supplied studentBid.
 * May return null if there are no test events, or the supplied student bid is not within the list of students.
 */
const studentTermsWithTestEvents = (studentList, studentBid) => {
    return studentList ? studentList.getIn([studentBid, 'termIdsWithTestEvents'], null) : null;
};

export const termsWithTestEventsForStudent = [
    students,
    PartnerGetters.terms,
    RosterGetters.selectedStudentBid,
    (studentList, terms, studentBid) => {
        const studentTerms = studentTermsWithTestEvents(studentList, studentBid);
        if (!studentTerms || !terms) {
            return null;
        }

        let returnVal = terms.filter((term) => {
            return studentTerms.includes(term.get('termNumber'));
        });

        // Only add most recent term if termIds already exist
        return returnVal.unshift(TermUtils.mostRecentTerm());
    },
];

/**
 * Optionally filters the studentInfo list (retrieved from the supplied studentList->studentBid), removing terms
 * which have no test events.  (note if no test events exist at all - all term data is left intact.)
 * @param studentList The overall list of students
 * @param studentBid The unique identifier for the student being sought
 * @param includeInfoForTermsWithNoTestEvents Boolean flag indicating whether or not to filter out terms with no
 * test events.  If set to true no terms will be filtered, otherwise terms which have no test events will be removed
 * from the list which is returned.
 * @returns The student info for the supplied studentBid (optionally filtered to only have info for terms with test events).
 * If there are no test events at all for a student all terms will be left intact. May return null if the supplied
 * studentBid is not within the list of students.
 */
const filterStudentInfoTerms = (studentList, studentBid, includeInfoForTermsWithNoTestEvents) => {
    let studentInfo = studentList ? studentList.getIn([studentBid, 'studentInfo'], null) : null;

    if (studentInfo && !includeInfoForTermsWithNoTestEvents) {
        let termsWithTestEvents = studentTermsWithTestEvents(studentList, studentBid);
        if (termsWithTestEvents && termsWithTestEvents.size > 0) {
            studentInfo = studentInfo.filter((value) => {
                return termsWithTestEvents.includes(value.get('termNumber'));
            });
        } //If there are no terms with test events leave the list intact.
    }
    return studentInfo;
};

export const studentInfoForCurrentStudentBid = [
    students,
    RosterGetters.selectedStudentBid,
    (studentList, sBid) => filterStudentInfoTerms(studentList, sBid, false),
];

export const studentInfo = (studentBid, includeInfoForTermsWithNoTestEvents = true) => {
    return [
        students,
        (studentList) => filterStudentInfoTerms(studentList, studentBid, includeInfoForTermsWithNoTestEvents),
    ];
};

export const studentExistsInStore = (studentBid) => {
    return [students, (s) => !!s && s.has(studentBid)];
};

const getTermDataForStudentBid = (studentList, studentBid) => {
    if (!studentList) return null;
    return studentList.getIn([studentBid, 'termData'], null);
};

export const termDataForStudentBid = (studentBid) => {
    return [
        students,
        (studentList) => {
            return getTermDataForStudentBid(studentList, studentBid);
        },
    ];
};

export const termDataForCurrentStudent = [
    students,
    RosterGetters.selectedStudentBid,
    (studentList, studentBid) => {
        return getTermDataForStudentBid(studentList, studentBid);
    },
];

export const growthProjectionsForCurrentStudentAndSubject = [
    students,
    RosterGetters.selectedStudentBid,
    RuntimeGetters.selectedSubject,
    (studentList, studentBid, selectedSubject) => {
        if (!studentList) {
            return null;
        }
        return studentList.getIn([studentBid, 'growthProjections', selectedSubject.get('bid')], null);
    },
];

export const growthProjectionsForStudentBidAndSubjectBid = (studentBid, subjectBid) => {
    return [store, 'students', studentBid, 'growthProjections', subjectBid];
};

export const growthPercentilesForStudentAndSubject = (subjectBid) => {
    return [
        students,
        RosterGetters.selectedStudentBid,
        (studentList, studentBid) => {
            if (!studentList) {
                return null;
            }

            return studentList.getIn([studentBid, 'growthPercentiles', subjectBid], null);
        },
    ];
};

export const growthPercentilesForKeystoneTestEvent = [
    students,
    RosterGetters.selectedStudentBid,
    getKeystoneTestEvent,
    (studentList, studentBid, testEvent) => {
        if (!studentList || !testEvent) {
            return null;
        }

        return studentList.getIn([studentBid, 'growthPercentiles', testEvent.get('measurementScaleID')], null);
    },
];

export const growthPercentilesForStudentAndKeystoneTestEvent = (courseSpecificBid) => {
    // courseSpecificBid is either the subject bid or measurementScaleID
    // depending on isCourseSpecific
    return [
        students,
        RosterGetters.selectedStudentBid,
        (studentList, studentBid) => {
            if (!studentList) {
                return null;
            }
            return studentList.getIn([studentBid, 'growthPercentiles', courseSpecificBid], null);
        },
    ];
};
export const growthPercentilesForStudentAndKeystoneTestEventBatchPrint = (studentBid, courseSpecificBid) => {
    // courseSpecificBid is either the subject bid or measurementScaleID
    // depending on isCourseSpecific
    return [
        students,
        (studentList) => {
            if (!studentList) {
                return null;
            }
            return studentList.getIn([studentBid, 'growthPercentiles', courseSpecificBid], null);
        },
    ];
};

export const growthPercentilesForStudentBidAndSubjectBid = (studentBid, subjectBid) => {
    return [store, 'students', studentBid, 'growthPercentiles', subjectBid];
};

export const linkingStudiesForCurrentStudentAndSubject = [
    students,
    RosterGetters.selectedStudentBid,
    getKeystoneTestEvent,
    (studentList, studentBid, keystoneTestEvent) => {
        if (!studentList || !keystoneTestEvent) {
            return null;
        }
        return studentList.getIn([studentBid, 'linkingStudies', keystoneTestEvent.get('measurementScaleID')], null);
    },
];

export const growthGoalsForCurrentStudentAndCourse = [
    students,
    getKeystoneTestEvent,
    (studentList, testEvent) => {
        if (!studentList || !testEvent) {
            return null;
        }
        return studentList.getIn(
            [testEvent.get('studentBid'), 'growthGoals', testEvent.get('measurementScaleID')],
            null
        );
    },
];

/* :CSN - Used in following places
 * app/utils/batch-chart.js
 * data-access-utils.js */
export const growthGoalsForStudentBidAndSubjectBid = (studentBid, subjectBid) => {
    return [store, 'students', studentBid, 'growthGoals', subjectBid];
};

const getGrowthGoalNorms = (studentList, studentBid) => {
    if (!studentList || !studentBid) {
        return null;
    }

    return studentList.getIn([studentBid, 'normsForGrowthGoals'], null);
};

export const growthGoalNormsForStudentBid = (studentBid) => {
    return [
        students,
        (studentList) => {
            return getGrowthGoalNorms(studentList, studentBid);
        },
    ];
};

export const growthGoalNormsForCurrentStudent = [
    students,
    RosterGetters.selectedStudentBid,
    (studentList, studentBid) => {
        return getGrowthGoalNorms(studentList, studentBid);
    },
];

const getStudentLoaded = (studentList, studentBid) => {
    if (!studentList || !studentBid) {
        return false;
    }

    return studentList.getIn([studentBid, 'isLoaded'], false);
};

export const studentLoaded = (studentBid) => {
    return [
        students,
        (studentList) => {
            return getStudentLoaded(studentList, studentBid);
        },
    ];
};

export const currentStudentIsLoaded = [
    students,
    RosterGetters.selectedStudentBid,
    (s, sBid) => {
        return getStudentLoaded(s, sBid);
    },
];

export const studentByViewOrderIsLoaded = (viewOrderToCheck) => {
    const studentToCheck =
        viewOrderToCheck.toLowerCase() == 'next'
            ? findNextStudent
            : viewOrderToCheck.toLowerCase() == 'previous'
            ? findPreviousStudent
            : null;
    if (!studentToCheck) {
        throw new Error(
            "unexpected condition: view order should be 'next' or 'previous', but was '" + viewOrderToCheck + "'"
        );
    }

    return [
        RosterGetters.selectedStudentBid,
        RosterGetters.students,
        students,
        (currentBid, studentList, studentData) => {
            // studentData isn't used, but is here to help determine state changes.
            // Partner getters are there to force a refresh so that the following lines will execute when needed.
            const student = studentToCheck(studentList, currentBid);
            const bidToCheck = student ? student.get('studentBid', null) : null;
            return Reactor.evaluate(studentLoaded(bidToCheck));
        },
    ];
};

export const studentDataCleared = [
    students,
    PartnerGetters.ritQuintiles,
    PartnerGetters.districtAverages,
    LCGetters.learningContinuumData,
    (students, quintiles, averages, lc) => {
        return students.isEmpty() && quintiles.isEmpty() && averages.isEmpty() && lc.isEmpty();
    },
];

const highlightsForStudentBid = (studentBid) => {
    const currentTerm = Reactor.evaluate(PartnerGetters.currentTerm);
    return [
        students,
        currentlySelectedTermNumber,
        (s, currentlySelectedTermNumber) => {
            if (
                currentlySelectedTermNumber == CommonValues.mostRecentTermNumber ||
                (currentTerm && currentlySelectedTermNumber === currentTerm.get('termNumber'))
            ) {
                return s ? s.getIn([studentBid, 'highlights'], null) : null;
            }
        },
    ];
};

const highlightsForCurrentStudent = [
    students,
    RosterGetters.selectedStudentBid,
    currentlySelectedTermNumber,
    PartnerGetters.currentTerm,
    (s, studentBid, currentlySelectedTermNumber, currentTerm) => {
        if (
            currentlySelectedTermNumber == CommonValues.mostRecentTermNumber ||
            (currentTerm && currentlySelectedTermNumber === currentTerm.get('termNumber'))
        ) {
            return s ? s.getIn([studentBid, 'highlights'], null) : null;
        }
    },
];

export const ritQuintilesLoadedForCurrentStudent = [
    students, // pulled in to ensure this is executed when it is needed
    RosterGetters.selectedStudentBid,
    PartnerGetters.ritQuintiles,
    (students, studentBid, quintiles) => {
        const termData = Reactor.evaluate(termDataForStudentBid(studentBid));
        return ritQuintilesLoaded(quintiles, termData, CommonValues.subjects);
    },
];

export const districtAveragesLoadedForCurrentStudent = [
    RosterGetters.selectedStudentBid,
    PartnerGetters.districtAverages,
    (studentBid, averages) => {
        const termData = Reactor.evaluate(termDataForStudentBid(studentBid));
        return districtAveragesLoaded(averages, termData, CommonValues.subjects);
    },
];

export const growthProjectionsLoadedForCurrentStudent = [
    students,
    RosterGetters.selectedStudentBid,
    (s, studentBid) => {
        return fieldLoadedForStudent(s, studentBid, 'growthProjections');
    },
];

export const linkingStudiesLoadedForCurrentStudentAndSubject = [
    students,
    RosterGetters.selectedStudentBid,
    RuntimeGetters.selectedSubject,
    (s, studentBid, selectedSubject) => {
        return s && s.hasIn([studentBid, 'linkingStudies', selectedSubject.get('bid')]);
    },
];

export const growthGoalNormsLoadedForCurrentStudent = [
    students,
    RosterGetters.selectedStudentBid,
    PartnerGetters.terms,
    (studentList, studentBid, terms) => {
        const growthGoalNorms = Reactor.evaluate(growthGoalNormsForStudentBid(studentBid));
        return growthGoalNormsLoaded(studentList, growthGoalNorms, CommonValues.subjects, studentBid, terms);
    },
];

export default {
    growthPercentilesForStudentAndKeystoneTestEventBatchPrint,
    allDataForStudent,
    currentStudentIsLoaded,
    currentlySelectedTermNumber,
    districtAveragesLoadedForCurrentStudent,
    getKeystoneTestEvent,
    growthGoalNormsForCurrentStudent,
    growthGoalNormsForStudentBid,
    growthGoalNormsLoadedForCurrentStudent,
    growthGoalsForCurrentStudentAndCourse,
    growthGoalsForStudentBidAndSubjectBid,
    growthPercentilesForKeystoneTestEvent,
    growthPercentilesForStudentAndKeystoneTestEvent,
    growthPercentilesForStudentAndSubject,
    growthPercentilesForStudentBidAndSubjectBid,
    growthProjectionsForCurrentStudentAndSubject,
    growthProjectionsForStudentBidAndSubjectBid,
    growthProjectionsLoadedForCurrentStudent,
    highlightsForCurrentStudent,
    highlightsForStudentBid,
    linkingStudiesForCurrentStudentAndSubject,
    linkingStudiesLoadedForCurrentStudentAndSubject,
    measurementScaleIDsForStudentByBid,
    nextStudent,
    previousStudent,
    ritQuintilesLoadedForCurrentStudent,
    studentByViewOrderIsLoaded,
    studentDataCleared,
    studentExistsInStore,
    studentInfo,
    studentInfoForCurrentStudentBid,
    studentLoaded,
    students,
    termDataForCurrentStudent,
    termDataForStudentBid,
    termsWithTestEventsForStudent,
    testEventsForCurrentStudent,
    testEventsForStudentBid,
};
